import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  COMMUNICATION_TYPE_NAME,
  CommunicationType,
  DEFAULT_EMAIL_DATA,
  DEFAULT_TEXT_DATA,
  IOneClickCommunicationDataModel,
  validationEmailSchema,
  validationTextSchema,
} from '@modules/OneClickEdit/interfaces';
import { PhoenixGridHeader } from '@modules/admin/Tools/LenderUtility/shared/PhoenixGridHeader';
import { useOneClickCommunicationData } from '@modules/OneClickEdit/hooks/useOneClickCommunicationData';
import { useOneClickEditColumns } from '@modules/OneClickEdit/hooks/useColumns';
import { useUsers } from '@shared/hooks/useUsers';
import { useToggle } from '@shared/hooks/useToggle';
import { getTabNumber } from '@shared/commonFunctions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { NavTabs } from '@shared/tabs/NavTabs';
import { Wrapper } from '@styles/CommonStyles';
import { IEmailTemplate, IEmailTemplates, ISMSTemplate, ISMSTemplates } from '@modules/admin/Tools/CustomerCommunications/types';
import { Header } from '../Header';

interface IOneClickEditTabsProps {
  setEmailToEdit: (row: IOneClickCommunicationDataModel) => void;
  setCurrentUser: (user: string | null) => void;
  setVoicemail: (mail: IOneClickCommunicationDataModel | null) => void;
  setEditEmailModalPosition: React.Dispatch<React.SetStateAction<number>>;
  setPreviewVoicemailPosition: React.Dispatch<React.SetStateAction<number>>;
  currentUser: string | null;
  smsTemplates: ISMSTemplates | null;
  emailTemplates: IEmailTemplates | null;
}

export const communicationTabsList = [
  { name: COMMUNICATION_TYPE_NAME.Text },
  { name: COMMUNICATION_TYPE_NAME.Email },
];

export const OneClickEditTabs = ({
  currentUser,
  setEmailToEdit,
  setEditEmailModalPosition,
  setCurrentUser,
  setVoicemail,
  setPreviewVoicemailPosition,
  smsTemplates,
  emailTemplates,
}: IOneClickEditTabsProps) => {
  const location = useLocation();
  const { users } = useUsers();
  const [editing] = useToggle();

  const communicationTypeId = useMemo(() => {
    let tabIndex = getTabNumber(location.search) - 1;
    tabIndex = tabIndex < 0 ? 0 : tabIndex;
    const tabName = communicationTabsList[tabIndex].name as keyof typeof CommunicationType;
    return CommunicationType[tabName];
  }, [location]);

  const { communicationData, isLoading } = useOneClickCommunicationData(communicationTypeId, currentUser);
  const { columns } = useOneClickEditColumns({
    communicationTypeId,
    onClickVoicemailPreview: () => {
      setVoicemail;
      setPreviewVoicemailPosition((prevState) => prevState + 1);
    },
  });

  const gridActions = useMemo(() => {
    if (communicationTypeId === CommunicationType.Voicemail) return;

    if (communicationTypeId === CommunicationType.Email || communicationTypeId === CommunicationType.Text) {
      return {
        onView: (row: IOneClickCommunicationDataModel) => {
          if (row.id === '0') {
            setEmailToEdit({ ...row, id: undefined });
          } else {
            setEmailToEdit(row);
          }
          setEditEmailModalPosition((prevState) => prevState + 1);
        },
      };
    }
  }, [communicationTypeId, setEmailToEdit]);

  
  const mergedCommunicationData = useMemo(() => {
    const merge = <T extends ISMSTemplate | IEmailTemplate>(
      defaultArray: IOneClickCommunicationDataModel[],
      templates: T[]
    ): IOneClickCommunicationDataModel[] => {
      return defaultArray.reduce<IOneClickCommunicationDataModel[]>((acc, curr) => {
        const item = communicationData?.find((communication) => communication.daysRangeBegin === curr.daysRangeBegin);

        const matchedTemplate = item?.templateId ? templates.find((template) => template.id === item.templateId) ?? null : null;

        return item
          ? [
              ...acc,
              {
                ...item,
                template: matchedTemplate,
              },
            ]
          : [...acc, curr];
      }, []);
    };
    switch (communicationTypeId) {
      case CommunicationType.Text:
        return merge(DEFAULT_TEXT_DATA, smsTemplates ?? []);
      case CommunicationType.Email:
        return merge(DEFAULT_EMAIL_DATA, emailTemplates ?? []);
      default:
        return communicationData;
    }
  }, [communicationData, communicationTypeId, smsTemplates, emailTemplates]);

  const gridValidationSchema = useMemo(() => {
    switch (communicationTypeId) {
      case CommunicationType.Text:
        return validationTextSchema;
      case CommunicationType.Email:
        return validationEmailSchema;
      default:
        return;
    }
  }, [communicationTypeId]);

  const tabs = useMemo(
    () =>
      communicationTabsList.map((tab) => ({
        ...tab,
        component: (
          <Wrapper>
            <PhoenixGrid
              autoHeight
              canNotCopy
              noButton
              strict
              withHeader
              canNotEdit
              loading={isLoading}
              isRowSelectable={() => false}
              validateSchema={gridValidationSchema}
              actions={gridActions}
              checkboxSelection={editing}
              columns={columns}
              rows={mergedCommunicationData ?? []}
            />
          </Wrapper>
        ),
      })),
    [communicationTypeId, gridActions, editing, columns, isLoading, mergedCommunicationData]
  );

  const sortedUsers = useMemo(() => users?.groupUsers?.sort((a, b) => a.userName.localeCompare(b.userName)), [users?.groupUsers]);

  return (
    <>
      <PhoenixGridHeader title="One-Click Edit">
        <Header
          currentUser={currentUser}
          users={sortedUsers || []}
          onChangeUser={setCurrentUser}
        />
      </PhoenixGridHeader>

      <NavTabs
        loading={false}
        data={tabs}
      />
    </>
  );
};
